import { transcribeAudio } from '../api.ts';
import type { SubtitleLine } from '../types.ts';

// ─── Subtitle Manager ─────────────────────────────────────────────────────────

export class SubtitleManager {
  private originalEl: HTMLElement;
  private translationEl: HTMLElement;
  private statusEl: HTMLElement;
  private overlayEl: HTMLElement;

  private enabled = true;
  private mediaRecorder: MediaRecorder | null = null;
  private audioStream: MediaStream | null = null;
  private pollInterval: ReturnType<typeof setInterval> | null = null;
  private displayQueue: SubtitleLine[] = [];
  private displayTimeout: ReturnType<typeof setTimeout> | null = null;
  private targetLanguage = 'en';

  constructor() {
    this.originalEl = document.getElementById('subtitle-original')!;
    this.translationEl = document.getElementById('subtitle-translation')!;
    this.statusEl = document.getElementById('subtitle-status')!;
    this.overlayEl = document.getElementById('subtitles-overlay')!;

    // Attempt to capture the user's preferred language
    this.targetLanguage = navigator.language.slice(0, 2) || 'en';
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.overlayEl.style.display = enabled ? '' : 'none';
    if (!enabled) this.stop();
  }

  /**
   * Attach to an HTMLAudioElement and start capturing audio chunks for
   * transcription.  Falls back to a status message when capture is not
   * possible (CORS-restricted streams).
   */
  attachToAudio(audio: HTMLAudioElement): void {
    this.stop();
    if (!this.enabled) return;

    try {
      // Try to capture via the AudioContext → MediaStreamDestination route
      const ctx = new AudioContext();
      const src = ctx.createMediaElementSource(audio);
      const dest = ctx.createMediaStreamDestination();
      src.connect(dest);
      src.connect(ctx.destination); // still route to speakers

      this.audioStream = dest.stream;
      this.startRecording(this.audioStream);
    } catch {
      this.showStatus('🔒 Subtitle capture unavailable for this stream');
    }
  }

  /** Called when the stream URL changes — restart capture */
  reset(): void {
    this.stop();
    this.clear();
  }

  stop(): void {
    this.mediaRecorder?.stop();
    this.mediaRecorder = null;
    if (this.pollInterval) clearInterval(this.pollInterval);
    this.pollInterval = null;
  }

  private startRecording(stream: MediaStream): void {
    try {
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : 'audio/webm';

      this.mediaRecorder = new MediaRecorder(stream, { mimeType });

      const chunks: Blob[] = [];
      this.mediaRecorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      this.mediaRecorder.onstop = () => {
        if (!this.enabled || chunks.length === 0) return;
        const blob = new Blob(chunks, { type: mimeType });
        void this.sendForTranscription(blob);
        chunks.length = 0;
      };

      // Capture in 7-second slices
      this.mediaRecorder.start(7_000);
      this.showStatus('🎙 AI subtitles active');

      // Restart capture loop
      this.pollInterval = setInterval(() => {
        if (this.mediaRecorder?.state === 'recording') {
          this.mediaRecorder.requestData();
        }
      }, 7_000);
    } catch {
      this.showStatus('⚠ Cannot capture audio — subtitles unavailable');
    }
  }

  private async sendForTranscription(blob: Blob): Promise<void> {
    try {
      const buffer = await blob.arrayBuffer();
      const base64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));

      const result = await transcribeAudio({
        audio: base64,
        targetLanguage: this.targetLanguage !== 'en' ? this.targetLanguage : undefined,
      });

      if (result.error) {
        this.showStatus(`⚠ ${result.error}`);
        return;
      }

      if (result.text.trim()) {
        this.enqueue({
          text: result.text,
          translation: result.translation,
          sourceLanguage: result.language ?? 'unknown',
          timestamp: Date.now(),
        });
      }
    } catch {
      this.showStatus('⚠ Subtitle service unreachable');
    }
  }

  private enqueue(line: SubtitleLine): void {
    this.displayQueue.push(line);
    if (this.displayQueue.length === 1) this.showNext();
  }

  private showNext(): void {
    if (this.displayQueue.length === 0) return;
    const line = this.displayQueue.shift()!;
    this.display(line);
    this.displayTimeout = setTimeout(() => this.showNext(), 6_000);
  }

  private display(line: SubtitleLine): void {
    this.statusEl.textContent = '';
    this.originalEl.textContent = line.text;
    this.translationEl.textContent = line.translation ?? '';

    // Fade animation — remove and re-add class
    this.originalEl.classList.remove('subtitle-fade-in');
    void this.originalEl.offsetWidth; // force reflow
    this.originalEl.classList.add('subtitle-fade-in');
  }

  private showStatus(msg: string): void {
    this.originalEl.textContent = '';
    this.translationEl.textContent = '';
    this.statusEl.textContent = msg;
  }

  private clear(): void {
    this.originalEl.textContent = '';
    this.translationEl.textContent = '';
    this.statusEl.textContent = '';
    this.displayQueue = [];
    if (this.displayTimeout) clearTimeout(this.displayTimeout);
  }
}
